import { FC, MouseEvent, ReactNode } from 'react'
import _ from 'lodash'
import { Link as ReactRouterLink } from 'react-router-dom'
import classnames from 'classnames'

import generateGuideSlug from '~/lib/generateGuideSlug'
import { useWithTrackLinkClick } from '../useWithTrackLinkClick'

import styles from './styles.css'

type LinkPropsType = {
    children: ReactNode
    className?: string | null
    'data-test-target'?: string
    disabled?: boolean | null
    error?: boolean
    href?: string | null
    key?: string | number
    name?: string
    onClick?: ((event: any) => any) | Function
    placement?: string | null
    style?: { [key: string]: any }
    target?: string
    to?: string | null
    underlined?: boolean
    warning?: boolean
    from?: any
    search?: string
    hash?: string
    rel?: string
    // questionable property
    title?: string
    // questionable property
    type?: string
}

const OMIT = ['dispatch']

export const Link: FC<LinkPropsType> = props => {
    const { onClick, disabled, underlined, error, warning } = props
    const {
        children,
        from,
        href = '',
        name,
        className,
        placement,
        target = '_self',
        to = '',
        search = '',
        hash = '',
        ...restOfProps
    } = props

    const handleOnClick = (evt: MouseEvent) => {
        if (disabled) {
            evt.preventDefault()
            return false
        }

        if (typeof onClick === 'function') {
            return onClick(evt)
        }

        return true
    }

    const onClickWithTrackLink = useWithTrackLinkClick(props, handleOnClick)

    const newProps = _.omit(restOfProps, OMIT)
    // todo, convert to typescript
    const guidename = generateGuideSlug(placement, name)
    const newClassName = classnames(
        guidename,
        className,
        disabled && styles.disabled,
        underlined && styles.underlined,
        error ? styles.error : warning && styles.warning,
    )

    //this is for browser security :+1:
    const rel = target === '_blank' ? 'noopener noreferrer' : ''

    // If no `to` is specified we need to make it an <a> tag b/c react-router no longer does
    // Note that react-router doesn't support target="_blank" so if you want your link to open
    // in a new window you should use href instead of to.
    if (href || !to) {
        return (
            <a
                href={disabled ? undefined : href ?? undefined}
                className={newClassName}
                target={target}
                rel={rel}
                {...newProps}
                onClick={onClickWithTrackLink}
                data-name={name}
                data-placement={placement}
            >
                {children}
            </a>
        )
    }

    const urlParts = new URL(to, location.origin)

    return (
        <ReactRouterLink
            to={{
                pathname: urlParts.pathname,
                hash: hash || urlParts.hash,
                search: search || urlParts.search,
            }}
            state={{ from }}
            {...newProps}
            onClick={onClickWithTrackLink}
            className={newClassName}
            data-name={name}
            data-placement={placement}
        >
            {children}
        </ReactRouterLink>
    )
}

export default Link
